import React, { useMemo, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { useLocation, useNavigate } from 'react-router-dom';

import {
  Box, FormControl, MenuItem, OutlinedInput, Paper, Select, Stack, Typography,
} from '@mui/material';

import { useRole } from '../../../auth';

import { usePagePresentation } from '../../../context/PagePresentationContext';

import { SCROLLABLE_SELECT_MENU_PROPS } from '../../../shared/components/selectMenuProps';

import { managementReportingYearOptions } from '../../../shared/utils/reportingYear';

import AppToast from '../../../shared/components/AppToast';

import { serializeActionPlanDraft, boundaryTypeForScope } from './actionPlanModel';

import { parseClimateActionRecommendationsContext } from '../model/climateActionNavigation';

import useClimateActionDashboardSlice from '../hooks/useClimateActionDashboardSlice';

import useClimateAiRun from '../hooks/useClimateAiRun';

import ClimateActionDashboard from './ClimateActionDashboard';

const OPERATIONAL_REDUCTION = 'OPERATIONAL_REDUCTION';

const normalizeDashboardAction = action => ({
  actionId: action.action_id ?? action.actionId,
  title: action.title,
  scope: action.scope,
  status: action.status,
  accountingClassification: action.accounting_classification ?? action.accountingClassification,
  verifiedTco2e: action.verified_tco2e ?? action.verifiedTco2e,
  isVerified: action.is_verified ?? action.isVerified,
});

/** Render the Climate Action dashboard for admins and viewers. */
export default function ClimateActionPage() {
  const { t, i18n } = useTranslation();
  const { isAdmin } = useRole();
  const navigate = useNavigate();
  const location = useLocation();

  const recommendationContext = useMemo(
    () => parseClimateActionRecommendationsContext(location.search),
    [location.search],
  );

  const [selectedYear, setSelectedYear] = useState(
    () => recommendationContext?.year || new Date().getUTCFullYear(),
  );
  const { slice: dashboardSlice } = useClimateActionDashboardSlice(selectedYear);
  const aiRun = useClimateAiRun(selectedYear, isAdmin);
  const [toast, setToast] = useState(null);

  const setSelectedYearRef = useRef(setSelectedYear);
  setSelectedYearRef.current = setSelectedYear;

  const yearOptions = useMemo(
    () => managementReportingYearOptions({ yearsAhead: 1 }),
    [],
  );

  usePagePresentation({ loading: false, status: 'COMPLETE' });

  const dashboardPosition = useMemo(() => {
    if (!dashboardSlice) {
      return {
        gross: 0, target: 0, gap: 0, inventory: {}, varianceCaption: null, isHistoricalTarget: false,
      };
    }
    const gross = dashboardSlice.gross_emissions_tco2e ?? 0;
    const target = dashboardSlice.emissions_target_tco2e ?? 0;
    return {
      gross,
      target,
      gap: dashboardSlice.variance_tco2e ?? 0,
      inventory: {},
      varianceCaption: dashboardSlice.variance_caption ?? null,
      isHistoricalTarget: Boolean(dashboardSlice.is_historical_target),
    };
  }, [dashboardSlice]);

  const dashboardActions = useMemo(
    () => (dashboardSlice?.actions || []).map(normalizeDashboardAction),
    [dashboardSlice],
  );

  const openActionPlan = (filter = 'all') => {
    navigate(`/climate-management?tab=action-plan&fy=${selectedYear}&filter=${filter}`);
  };

  const changeYear = (event) => {
    setSelectedYear(Number(event.target.value));
  };

  const clearRecommendationContext = () => {
    navigate('/climate-action');
  };

  const saveRecommendationAsDraft = async (recommendation) => {
    const boundary = recommendation.boundary || {};
    const boundaryName = boundary.name || boundary.ref_id || '';
    const title = boundaryName
      ? `${recommendation.solution_name} — ${boundaryName}`
      : recommendation.solution_name;
    const basisNote = t('climateAction.ai.basisNote', {
      percent: recommendation.reduction_percent ?? '',
      boundary: boundaryName,
    });
    const draft = serializeActionPlanDraft({
      title,
      description: recommendation.benefit || recommendation.rationale || null,
      actionType: OPERATIONAL_REDUCTION,
      scope: recommendation.scope,
      category: recommendation.category,
      subCategory: recommendation.sub_category ?? null,
      boundaryType: boundaryTypeForScope(recommendation.scope),
      boundaryRefId: boundary.ref_id ?? null,
      startFiscalYear: selectedYear,
      endFiscalYear: selectedYear,
      planningEstimates: recommendation.estimate_status === 'NEEDS_DATA'
        ? []
        : [{
          fiscalYear: selectedYear,
          reductionTco2ePerYear: recommendation.estimated_tco2e,
          basisNote,
        }],
      aiRecommendationId: recommendation.ai_recommendation_id,
    });
    return aiRun.saveAsDraft(draft);
  };

  const dashboardView = (
    <ClimateActionDashboard
      actionImpact={dashboardSlice?.climate_action_impact || {}}
      actions={dashboardActions}
      aiRun={aiRun.run}
      aiRunGenerating={aiRun.generating}
      canMutate={isAdmin}
      showAiRecommendations={isAdmin}
      carbonCredit={dashboardSlice?.carbon_credit || {}}
      context={recommendationContext}
      locale={i18n.language}
      onClearContext={clearRecommendationContext}
      onDismissSource={aiRun.dismiss}
      onGenerate={aiRun.generate}
      onSaveAsDraft={saveRecommendationAsDraft}
      onToast={setToast}
      onUndoDismissSource={aiRun.undoDismiss}
      onViewActionPlan={openActionPlan}
      otherClimateImpact={dashboardSlice?.other_climate_impact || {}}
      pathway={dashboardSlice?.pathway}
      position={dashboardPosition}
      selectedYear={selectedYear}
      t={t}
    />
  );

  return (
    <Box
      component="section" aria-labelledby="climate-action-title" className="page-stack climate-action-shell"
      sx={{
        backgroundColor: 'background.default',
        fontSize: 14,
        lineHeight: 1.5,
        minHeight: '100dvh',
        minWidth: 0,
        mx: -3.5,
        mt: -2,
        px: 3.5,
        pt: 2,
        '& .MuiButton-root, & .MuiInputBase-root, & .MuiTab-root': {
          fontSize: 13,
        },
      }}
    >
      <Paper
        variant="outlined"
        sx={{
          backgroundColor: theme => (theme.palette.mode === 'dark' ? 'background.paper' : '#FFFFFF'),
          borderRadius: 1.5,
          boxShadow: 'none',
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: 'space-between',
          mb: 2,
          px: { xs: 2, md: 3 },
          py: 2.25,
          gap: 2,
        }}
      >
        <Stack spacing={0.5} sx={{ minWidth: 0 }}>
          <Typography id="climate-action-title" component="h1" sx={{ fontSize: { xs: 26, md: 30 }, fontWeight: 700, letterSpacing: '-0.025em' }}>
            {t('climateAction.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
            {t('climateAction.subtitle')}
          </Typography>
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} spacing={1} sx={{ flexShrink: 0 }}>
          <FormControl size="small" sx={{ minWidth: { sm: 116 }, width: { xs: '100%', sm: 'auto' } }}>
            <Select
              MenuProps={SCROLLABLE_SELECT_MENU_PROPS}
              value={selectedYear}
              onChange={changeYear}
              input={(
                <OutlinedInput
                  size="small"
                />
              )}
              inputProps={{ 'aria-label': t('climateAction.yearLabel') }}
              sx={{ width: '100%' }}
            >
              {yearOptions.map(year => <MenuItem key={year} value={year}>{year}</MenuItem>)}
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      {dashboardView}

      <AppToast onClose={() => setToast(null)} toast={toast} />
    </Box>
  );
}